import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Container } from '../style';
import {
  Building2, Scale, Cpu, Calculator, User,
  Phone, ChevronDown, Users, Briefcase
} from 'lucide-react';

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f7f8fa;
  padding: 48px 0 72px;
`;

const Hero = styled.div`
  background: #1a365d;
  border-radius: 14px;
  padding: 44px 52px;
  margin-bottom: 36px;
  animation: ${fadeUp} 0.4s ease both;
  @media (max-width: 768px) { padding: 28px 24px; }
`;

const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.7);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  padding: 5px 12px;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const HeroTitle = styled.h1`
  color: #fff;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 10px;
`;

const HeroDesc = styled.p`
  color: rgba(255,255,255,0.55);
  font-size: 14px;
  margin: 0;
  line-height: 1.65;
  max-width: 560px;
`;

// ─── Chairman (special top card) ─────────────────────────────────────────────

const ChairmanCard = styled.div`
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e2eaf4;
  padding: 28px 32px;
  margin-bottom: 28px;
  display: flex;
  align-items: center;
  gap: 24px;
  animation: ${fadeUp} 0.4s ease both;
  animation-delay: 0.05s;
  @media (max-width: 640px) { flex-direction: column; align-items: flex-start; gap: 16px; }
`;

const ChairmanAvatar = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: #1a365d;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
`;

const ChairmanInfo = styled.div`
  flex: 1;
`;

const ChairmanRole = styled.p`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: #718096;
  margin: 0 0 5px;
`;

const ChairmanName = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1a365d;
  margin: 0 0 6px;
`;

const ChairmanPosition = styled.p`
  font-size: 13px;
  color: #4a5568;
  margin: 0 0 8px;
`;

const ContactBadge = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #2c5282;
  font-weight: 600;
  text-decoration: none;
  background: #ebf4ff;
  padding: 5px 12px;
  border-radius: 6px;
  transition: background 0.15s;
  &:hover { background: #bee3f8; }
`;

// ─── Section label ────────────────────────────────────────────────────────────

const SectionLabel = styled.h2`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: #718096;
  margin: 0 0 12px;
  padding-left: 4px;
`;

// ─── Dept Grid ────────────────────────────────────────────────────────────────

const DeptGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  animation: ${fadeUp} 0.4s ease both;
  animation-delay: 0.1s;

  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

// ─── Dept Card ────────────────────────────────────────────────────────────────

const DeptCard = styled.div`
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2eaf4;
  overflow: hidden;
  transition: box-shadow 0.2s;
  &:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.07); }
`;

const DeptHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 22px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  border-bottom: ${p => p.$open ? '1px solid #eef1f6' : 'none'};
  transition: background 0.15s;
  &:hover { background: #f9fafb; }
`;

const DeptIconWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${p => p.$bg || '#ebf4ff'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${p => p.$color || '#2c5282'};
`;

const DeptHeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const DeptTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: #1a365d;
  margin: 0;
`;

const DeptSubtitle = styled.p`
  font-size: 12px;
  color: #a0aec0;
  margin: 2px 0 0;
`;

const ChevronIcon = styled(ChevronDown)`
  color: #a0aec0;
  flex-shrink: 0;
  transition: transform 0.2s;
  transform: ${p => p.$open ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const DeptBody = styled.div`
  display: ${p => p.$open ? 'block' : 'none'};
  padding: 18px 22px 20px;
  animation: ${slideDown} 0.2s ease;
`;

const MemberRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const MemberAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: #f0f4ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #2c5282;
`;

const MemberInfo = styled.div`
  flex: 1;
`;

const MemberName = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #1a365d;
  margin: 0 0 3px;
`;

const MemberPosition = styled.p`
  font-size: 13px;
  color: #718096;
  margin: 0 0 8px;
  line-height: 1.5;
`;

const MemberContact = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #2c5282;
  font-weight: 600;
  text-decoration: none;
  background: #ebf4ff;
  padding: 4px 10px;
  border-radius: 5px;
  transition: background 0.15s;
  &:hover { background: #bee3f8; }
`;

// ─── Config ───────────────────────────────────────────────────────────────────

const DEPT_CONFIG = {
  accounting: { icon: Calculator, bg: '#fef3c7', color: '#92400e' },
  bankingDept: { icon: Building2,  bg: '#e6fffa', color: '#276749' },
  legalDept:   { icon: Scale,      bg: '#fce8f3', color: '#97266d' },
  itDept:      { icon: Cpu,        bg: '#ebf4ff', color: '#2c5282' },
};

// ─── Component ────────────────────────────────────────────────────────────────

const OrgStructure = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState({});
  const toggle = (key) => setOpen(prev => ({ ...prev, [key]: !prev[key] }));

  const depts = Object.keys(DEPT_CONFIG);

  return (
    <PageWrapper>
      <Container>

        <Hero>
          <HeroBadge>
            <Users size={11} />
            {t('orgStructure.badge', 'Организация')}
          </HeroBadge>
          <HeroTitle>{t('orgStructure.title')}</HeroTitle>
          <HeroDesc>{t('orgStructure.description')}</HeroDesc>
        </Hero>

        {/* Chairman */}
        <ChairmanCard>
          <ChairmanAvatar>
            <Briefcase size={26} />
          </ChairmanAvatar>
          <ChairmanInfo>
            <ChairmanRole>{t('orgStructure.sections.chairman.title')}</ChairmanRole>
            <ChairmanName>{t('orgStructure.sections.chairman.name')}</ChairmanName>
            <ChairmanPosition>{t('orgStructure.sections.chairman.position')}</ChairmanPosition>
            <ContactBadge href={`tel:${t('orgStructure.sections.chairman.contact')}`}>
              <Phone size={12} />
              {t('orgStructure.sections.chairman.contact')}
            </ContactBadge>
          </ChairmanInfo>
        </ChairmanCard>

        {/* Departments */}
        <SectionLabel>{t('orgStructure.sections.units')}</SectionLabel>
        <DeptGrid>
          {depts.map((key) => {
            const { icon: Icon, bg, color } = DEPT_CONFIG[key];
            const isOpen = !!open[key];
            return (
              <DeptCard key={key}>
                <DeptHeader $open={isOpen} onClick={() => toggle(key)}>
                  <DeptIconWrap $bg={bg} $color={color}>
                    <Icon size={18} />
                  </DeptIconWrap>
                  <DeptHeaderText>
                    <DeptTitle>{t(`orgStructure.sections.${key}.title`)}</DeptTitle>
                    <DeptSubtitle>{t(`orgStructure.sections.${key}.name`)}</DeptSubtitle>
                  </DeptHeaderText>
                  <ChevronIcon size={16} $open={isOpen} />
                </DeptHeader>

                {isOpen && (
                  <DeptBody $open={isOpen}>
                    <MemberRow>
                      <MemberAvatar><User size={16} /></MemberAvatar>
                      <MemberInfo>
                        <MemberName>{t(`orgStructure.sections.${key}.name`)}</MemberName>
                        <MemberPosition>{t(`orgStructure.sections.${key}.position`)}</MemberPosition>
                        <MemberContact href={`tel:${t(`orgStructure.sections.${key}.contact`)}`}>
                          <Phone size={11} />
                          {t(`orgStructure.sections.${key}.contact`)}
                        </MemberContact>
                      </MemberInfo>
                    </MemberRow>
                  </DeptBody>
                )}
              </DeptCard>
            );
          })}
        </DeptGrid>

      </Container>
    </PageWrapper>
  );
};

export default OrgStructure;